/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Modal, Accordion, Card, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { LabOrder } from '@dataTypes/lab.types'
import './LabOrderInfo.css'
import xray1 from '@assets/images/xray1.webp'
import xray2 from '@assets/images/xray2.webp'
import xray3 from '@assets/images/xray3.webp'
import xray4 from '@assets/images/xray4.webp'
import IPRToothChart from './IPRToothChart'
import LabSharedTable from '@components/pages/Lab/LabSharedTable'

export interface LabOrderInfoProps {
  show: boolean
  onClose: () => void
  labOrder: LabOrder | null
}

const imageData = [
  { url: xray1, name: 'xray1.webp', date: '2025-04-20', size: '1.2 MB' },
  { url: xray2, name: 'xray2.webp', date: '2025-04-20', size: '950 KB' },
  { url: xray3, name: 'xray3.webp', date: '2025-04-21', size: '2.1 MB' },
  { url: xray4, name: 'xray4.webp', date: '2025-04-21', size: '2.5 MB' },
]

const LabOrderInfo: React.FC<LabOrderInfoProps> = ({
  show,
  onClose,
  labOrder,
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const tableColumns = [
    {
      key: 'thumbnail',
      label: 'Preview',
      style: { width: '80px' },
      formatter: (_: any, row: any) => (
        <img
          src={row.url}
          alt={row.name}
          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
          className="rounded"
        />
      ),
    },
    { key: 'name', label: 'Name', style: { width: '200px' } },
    { key: 'date', label: 'Date', style: { width: '150px' } },
    { key: 'size', label: 'Size', style: { width: '100px' } },
    {
      key: 'actions',
      label: 'Actions',
      style: { width: '80px' },
      formatter: () => (
        <i className="ti ti-dots fs-5 cursor-pointer" title="Actions" />
      ),
    },
  ]

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      dialogClassName="modal-xxl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Lab order info</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Accordion
          defaultActiveKey={['0', '1']}
          alwaysOpen
          className="lab-order-info-accordion"
        >
          {/* Accordion 1: Images */}
          <Accordion.Item eventKey="0">
            <Accordion.Header>Images</Accordion.Header>
            <Accordion.Body>
              <div className="d-flex justify-content-end mb-2">
                <div className="btn-group lab-order-btn-group">
                  <button
                    className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_10566_17657)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2 3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H7.33333V7.33333H2V3.33333ZM7.33333 8.66667H2V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H7.33333V8.66667ZM8.66667 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8.66667H8.66667V14ZM8.66667 7.33333V2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V7.33333H8.66667Z"
                          fill="#112850"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_10566_17657">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="ps-1">Grid</span>
                  </button>
                  <button
                    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 11.6668C3.26522 11.6668 3.51957 11.7722 3.70711 11.9597C3.89464 12.1473 4 12.4016 4 12.6668C4 12.932 3.89464 13.1864 3.70711 13.3739C3.51957 13.5615 3.26522 13.6668 3 13.6668C2.73478 13.6668 2.48043 13.5615 2.29289 13.3739C2.10536 13.1864 2 12.932 2 12.6668C2 12.4016 2.10536 12.1473 2.29289 11.9597C2.48043 11.7722 2.73478 11.6668 3 11.6668ZM13.3333 12.0002C13.5101 12.0002 13.6797 12.0704 13.8047 12.1954C13.9298 12.3204 14 12.49 14 12.6668C14 12.8436 13.9298 13.0132 13.8047 13.1382C13.6797 13.2633 13.5101 13.3335 13.3333 13.3335H6C5.82319 13.3335 5.65362 13.2633 5.5286 13.1382C5.40357 13.0132 5.33333 12.8436 5.33333 12.6668C5.33333 12.49 5.40357 12.3204 5.5286 12.1954C5.65362 12.0704 5.82319 12.0002 6 12.0002H13.3333ZM3 7.00016C3.26522 7.00016 3.51957 7.10552 3.70711 7.29306C3.89464 7.48059 4 7.73495 4 8.00016C4 8.26538 3.89464 8.51973 3.70711 8.70727C3.51957 8.89481 3.26522 9.00016 3 9.00016C2.73478 9.00016 2.48043 8.89481 2.29289 8.70727C2.10536 8.51973 2 8.26538 2 8.00016C2 7.73495 2.10536 7.48059 2.29289 7.29306C2.48043 7.10552 2.73478 7.00016 3 7.00016ZM13.3333 7.3335C13.5033 7.33368 13.6667 7.39875 13.7902 7.51539C13.9138 7.63204 13.9882 7.79146 13.9981 7.96109C14.0081 8.13072 13.9529 8.29775 13.8438 8.42805C13.7348 8.55835 13.5801 8.64209 13.4113 8.66216L13.3333 8.66683H6C5.83008 8.66664 5.66665 8.60158 5.54309 8.48493C5.41953 8.36828 5.34518 8.20886 5.33522 8.03923C5.32526 7.8696 5.38045 7.70258 5.48951 7.57227C5.59857 7.44197 5.75327 7.35823 5.922 7.33816L6 7.3335H13.3333ZM3 2.3335C3.26522 2.3335 3.51957 2.43885 3.70711 2.62639C3.89464 2.81393 4 3.06828 4 3.3335C4 3.59871 3.89464 3.85307 3.70711 4.0406C3.51957 4.22814 3.26522 4.3335 3 4.3335C2.73478 4.3335 2.48043 4.22814 2.29289 4.0406C2.10536 3.85307 2 3.59871 2 3.3335C2 3.06828 2.10536 2.81393 2.29289 2.62639C2.48043 2.43885 2.73478 2.3335 3 2.3335ZM13.3333 2.66683C13.5033 2.66702 13.6667 2.73208 13.7902 2.84873C13.9138 2.96537 13.9882 3.1248 13.9981 3.29443C14.0081 3.46405 13.9529 3.63108 13.8438 3.76138C13.7348 3.89169 13.5801 3.97543 13.4113 3.9955L13.3333 4.00016H6C5.83008 3.99997 5.66665 3.93491 5.54309 3.81826C5.41953 3.70162 5.34518 3.54219 5.33522 3.37257C5.32526 3.20294 5.38045 3.03591 5.48951 2.90561C5.59857 2.77531 5.75327 2.69157 5.922 2.6715L6 2.66683H13.3333Z"
                        fill="#163DA2"
                      />
                    </svg>
                    <span className="ps-1">List</span>
                  </button>
                </div>
              </div>
              {/* Grid View */}
              {viewMode === 'grid' && (
                <Row className="g-3">
                  {imageData.map((img, idx) => (
                    <Col md={3} key={idx}>
                      <Card className="h-100">
                        <Card.Img
                          variant="top"
                          src={img.url}
                          alt={img.name}
                          style={{ height: '120px', objectFit: 'cover' }}
                        />
                        <Card.Body className="p-2 position-relative">
                          <div>
                            <div className="fw-bold">{img.name}</div>
                            <div className="text-muted small">
                              {img.date} • {img.size}
                            </div>
                          </div>
                          <div
                            className="position-absolute top-50 end-0 translate-middle-y me-2"
                            style={{ zIndex: 1 }}
                          >
                            <i
                              className="ti ti-dots fs-5 cursor-pointer"
                              title="Actions"
                            />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
              {/* List View */}
              {viewMode === 'list' && (
                <LabSharedTable
                  columns={tableColumns}
                  data={imageData}
                  pageSize={5}
                />
              )}
            </Accordion.Body>
          </Accordion.Item>

          {/* Accordion 2: IPR */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>Interproximal reduction (IPR)</Accordion.Header>
            <Accordion.Body>
              <IPRToothChart data={labOrder?.iprData} editable={false} />
            </Accordion.Body>
          </Accordion.Item>

          {/* Accordion 3: Comments */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>Comments</Accordion.Header>
            <Accordion.Body>
              {labOrder?.prescriptions?.some((p) => p.instructions?.trim()) ? (
                <div className="d-flex flex-column gap-2">
                  {labOrder.prescriptions
                    .filter((p) => p.instructions?.trim())
                    .map((p, index) => (
                      <div key={index} className="border-bottom pb-2">
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          <strong>{p.procedureType}</strong>: {p.instructions}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-muted">No lab instructions available.</div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <Button className="button-secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default LabOrderInfo
